import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map, Observable, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class RelatedPartyPaymentService {
  private baseUrl: string = '';

  constructor(private http: HttpClient) {}

  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  private getCoreGlobalParams(globalParams: any): any {
    const coreKeys = [
      'tax_year', 'scenario', 'jcd_key', 'issue_id', 'issue_short_desc', 
      'checkLockedbj', 'scenarioCode', 'is_issue_key_enabled'
    ];
    
    const coreParams: any = {};
    coreKeys.forEach((key: string) => {
      if (globalParams.hasOwnProperty(key)) {
        coreParams[key] = globalParams[key];
      }
    });
    
    return coreParams;
  }

  /**
   * Action code: 479nmu
   */
  getPaymentTypes(rowData: any, globalParams: any): Observable<any> {
    const params: any = {
      ...(globalParams || {}),
      action_code: '479nmu',
      payment_type: rowData?.PAYMENT_TYPE || ''
    };
    
    return this.http.get<any>(`${this.baseUrl}/loadJsonObject`, 
      { params, withCredentials: true }).pipe(
      map((response: any) => {
        if (response.callSuccess === '1' && response.jsonObject) {
          return response.jsonObject;
        } else {
          throw new Error(response.errorMessage || 'Failed to load payment types');
        }
      }),
      catchError((error) => throwError(error))
    );
  }

  /**
   * Action code: 3pqapp
   */
  getPayor(taxYear: string, globalParams: any): Observable<any> {
    const params: any = {
      ...(globalParams || {}),
      action_code: '3pqapp',
      tax_year: taxYear
    };
    
    return this.http.get<any>(`${this.baseUrl}/loadJsonObject`, 
      { params, withCredentials: true }).pipe(
      map((response: any) => {
        if (response.callSuccess === '1' && response.jsonObject) {
          return response.jsonObject;
        } else {
          throw new Error(response.errorMessage || 'Failed to load payor data');
        }
      }),
      catchError((error) => throwError(error))
    );
  }

  /**
   * Action code: o2nttw
   */
  getRecipients(taxYear: string, globalParams: any): Observable<any> {
    const params: any = {
      ...(globalParams || {}),
      action_code: 'o2nttw',
      tax_year: taxYear
    };
    
    return this.http.get<any>(`${this.baseUrl}/loadJsonObject`, 
      { params, withCredentials: true }).pipe(
      map((response: any) => {
        if (response.callSuccess === '1' && response.jsonObject) {
          return response.jsonObject;
        } else {
          throw new Error(response.errorMessage || 'Failed to load recipients');
        }
      }),
      catchError((error) => throwError(error))
    );
  }

  /**
   * Action code: 479nmu (saveJsonObject)
   */
  savePaymentType(rowData: any, paymentType: any, globalParams: any): Observable<any> {
    const coreParams = this.getCoreGlobalParams(globalParams || {});
    
    const formData: any = {
      ...coreParams,
      rp_seq_id: rowData.RP_SEQ_ID,
      payment_type: ' ',
      payment_sub_type: paymentType.PAYMENT_SUB_TYPE
    };
    
    const urlEncoded = new URLSearchParams();
    Object.keys(formData).forEach((key: string) => {
      const value = formData[key];
      if (value !== undefined && value !== null) {
        urlEncoded.set(key, value.toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    
    return this.http.post(`${this.baseUrl}/saveJsonObject?action_code=479nmu`, 
      urlEncoded.toString(), { headers, withCredentials: true });
  }

  /**
   * Action code: 7hra73
   */
  addNewPayment(paymentData: any[], clobSettings: any[], ssoId: string, checkLockObj: any[], globalParams: any, filterParams: any): Observable<any> {
    const jsonSettings = JSON.stringify(clobSettings);
    const jsonObj = JSON.stringify(paymentData);

    const params: any = {
      ...(globalParams || {}),
      ...(filterParams || {}),
      jsonObj: jsonObj,
      jsonSettings: jsonSettings,
      sso_id: ssoId,
      filing_group: 1
    };

    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach((key: string) => {
      const value = params[key];
      if (value !== undefined && value !== null) {
        urlEncoded.set(key, value.toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post(`${this.baseUrl}/saveJsonObject?action_code=7hra73`,
      urlEncoded.toString(), { headers, withCredentials: true });
  }

  /**
   * Action code: ipq04o
   */
  deletePayment(paymentData: any[], clobSettings: any[], ssoId: string, globalParams: any): Observable<any> {
    const gp = globalParams || {};
    const coreParams = this.getCoreGlobalParams(gp);
    
    const formDataParams: any = {
      ...coreParams,
      filing_key: gp.filing_key || 1,
      sso_id: ssoId,
      rp_seq_id: paymentData[0].rp_seq_id
    };

    const urlEncoded = new URLSearchParams();
    Object.keys(formDataParams).forEach((key: string) => {
      const value = formDataParams[key];
      if (value !== undefined && value !== null) {
        urlEncoded.set(key, value.toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post(`${this.baseUrl}/saveJsonObject?action_code=ipq04o`, 
      urlEncoded.toString(), { headers, withCredentials: true });
  }

  /**
   * Action code: q7a4cr
   */
  getPayorAccountDetails(
    le_issue_clob: any[],
    payment_type: string,
    classification_type: string,
    trans_type: string,
    sso_id: string,
    globalParams: any,
    filterParams: any
  ): Observable<any> {
    const cc_clob = JSON.stringify(le_issue_clob);
    const fp = filterParams || {};
    
    const params: any = {
      ...(globalParams || {}),
      ...fp,
      action_code: 'q7a4cr',
      sso_id: sso_id,
      cc_clob: cc_clob,
      payment_type: payment_type || fp.category_filter || '',
      classification_type: classification_type || fp.category_filter || '',
      trans_type: trans_type
    };
    
    if (!params.filing_key) {
      params.filing_key = 1;
    }
    
    return this.http.get<any>(`${this.baseUrl}/loadJsonObject`, 
      { params, withCredentials: true }).pipe(
      map((response: any) => {
        if (response.callSuccess === '1' && response.jsonObject) {
          return response;
        } else {
          throw new Error(response.errorMessage || 'Failed to load payor account details');
        }
      }),
      catchError((error) => throwError(error))
    );
  }

  /**
   * Action code: kgwkdu
   */
  getExtPayorDetails(globalParams: any): Observable<any> {
    const params: any = {
      ...(globalParams || {}),
      action_code: 'kgwkdu'
    };
    
    return this.http.get<any>(`${this.baseUrl}/loadJsonObject`, 
      { params, withCredentials: true }).pipe(
      map((response: any) => {
        if (response.callSuccess === '1' && response.jsonObject) {
          return response;
        } else {
          throw new Error(response.errorMessage || 'Failed to load external payor details');
        }
      }),
      catchError((error) => throwError(error))
    );
  }

  /**
   * Action code: bzsfqi
   */
  getPayorModalByAcct(
    cc_acct_clob: any[],
    acct_type: string,
    payment_type: string,
    classification_type: string,
    trans_type: string,
    sso_id: string,
    globalParams: any,
    filterParams: any
  ): Observable<any> {
    const fp = filterParams || {};
    const cc_clob = JSON.stringify(cc_acct_clob);
    
    const params: any = {
      ...(globalParams || {}),
      ...fp,
      action_code: 'bzsfqi',
      sso_id: sso_id,
      cc_clob: cc_clob,
      acct_type: acct_type,
      payment_type: payment_type || fp.category_filter || '',
      classification_type: classification_type || fp.category_filter || '',
      trans_type: trans_type
    };
    
    if (!params.filing_key) {
      params.filing_key = 1;
    }
    
    return this.http.get<any>(`${this.baseUrl}/loadJsonObject`, 
      { params, withCredentials: true }).pipe(
      map((response: any) => {
        if (response.callSuccess === '1' && response.jsonObject) {
          return response;
        } else {
          throw new Error(response.errorMessage || 'Failed to load payor modal data by account');
        }
      }),
      catchError((error) => throwError(error))
    );
  }

  /**
   * Action code: eosx7n
   */
  getM3PayorList(globalParams: any): Observable<any> {
    const params: any = {
      ...(globalParams || {}),
      action_code: 'eosx7n'
    };
    
    return this.http.get<any>(`${this.baseUrl}/loadJsonObject`, 
      { params, withCredentials: true }).pipe(
      map((response: any) => {
        if (response.callSuccess === '1' && response.jsonObject) {
          return response;
        } else {
          throw new Error(response.errorMessage || 'Failed to load M3 payor list');
        }
      }),
      catchError((error) => throwError(error))
    );
  }

  /**
   * Action code: y0onfe
   */
  saveRPModal(
    final_data: any[],
    clob_settings: any[],
    classification_type: string,
    trans_type: string,
    globalParams: any,
    filterParams: any
  ): Observable<any> {
    const json_settings = JSON.stringify(clob_settings);
    const json_data = JSON.stringify(final_data);
    
    const params: any = {
      ...(globalParams || {}),
      ...(filterParams || {}),
      json_data: json_data,
      json_settings: json_settings,
      classification_type: classification_type,
      trans_type: trans_type
    };
    
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach((key: string) => {
      const value = params[key];
      if (value !== undefined && value !== null) {
        if (typeof value === 'object') {
          urlEncoded.set(key, JSON.stringify(value));
        } else {
          urlEncoded.set(key, value.toString());
        }
      }
    });
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    
    return this.http.post(`${this.baseUrl}/saveJsonObject?action_code=y0onfe`, 
      urlEncoded.toString(), { headers, withCredentials: true }).pipe(
      map((response: any) => response),
      catchError((error) => throwError(error))
    );
  }

  /**
   * Action code: oexyp0
   */
  allocateProcess(
    allocateprocessdata: any[],
    clob_settings: any[],
    globalParams: any,
    filterParams: any
  ): Observable<any> {
    const json_settings = JSON.stringify(clob_settings);
    const json_data = JSON.stringify(allocateprocessdata);
    
    const params: any = {
      ...(globalParams || {}),
      ...(filterParams || {}),
      json_data: json_data,
      json_settings: json_settings
    };
    
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach((key: string) => {
      const value = params[key];
      if (value !== undefined && value !== null) {
        if (typeof value === 'object') {
          urlEncoded.set(key, JSON.stringify(value));
        } else {
          urlEncoded.set(key, value.toString());
        }
      }
    });
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    
    return this.http.post(`${this.baseUrl}/saveJsonObject?action_code=oexyp0`, 
      urlEncoded.toString(), { headers, withCredentials: true }).pipe(
      map((response: any) => response),
      catchError((error) => throwError(error))
    );
  }

  /**
   * Action code: eosx7n
   */
  searchTradingPartners(searchString: string, lineNo: string, globalParams: any): Observable<any> {
    const params: any = {
      ...(globalParams || {}),
      action_code: 'eosx7n',
      search_string: searchString,
      line_no: lineNo
    };
    
    return this.http.get<any>(`${this.baseUrl}/loadJsonObject`, 
      { params, withCredentials: true }).pipe(
      map((response: any) => {
        if (response.callSuccess === '1' && response.jsonObject) {
          return response.jsonObject;
        } else {
          return [];
        }
      }),
      catchError((error) => throwError(error))
    );
  }
}

